import { ITagInfo, UTagObjectName } from '../../customTypings/avant-config/tenantConfig';
import { AbstractAnalyticsLogger, IEventDisabled } from '../analyticsLogger';

export enum TagsSupported {
  Utag = 'utag',
  AmountTag = 'amount_tag'
}

interface IUtagBase {
  view (tags: ITagInfo): void;
  link (tags: ITagInfo): void;
}

/* eslint-disable */
declare global {
  interface Window {
    utag: IUtagBase;
    utag_data: any;
    utag_cfg_ovrd: any;
    amount_tag: IUtagBase;
    amount_tag_data: any;
    amount_tag_cfg_ovrd: any;
    tealium_enabled: boolean;
  }
}
/* eslint-enable */

interface ITealiumTags {
  pageSpecificTags?: ITagInfo;
  eventTags?: ITagInfo;
}

// Custom config values
const sendEventsToAmount: boolean = !!AvantConfig.TenantConfig.analytics?.sendEventsToAmount;
const commonPageTags: ITagInfo = AvantConfig.TenantConfig.analytics?.commonTags || {};
const tenantUtagObjectName: UTagObjectName | undefined = AvantConfig.TenantConfig.analytics?.utagObjectName;
const siteType: string | undefined = AvantConfig.TenantConfig.analytics?.siteType;
const TENANT = process.env.TENANT;
const TEALIUM_ENV = process.env.REACT_APP_TEALIUM_ENVIRONMENT;

const uTagEnabled = (): boolean => (
  !!(tenantUtagObjectName && window[tenantUtagObjectName])
);

const amountTagEnabled = (): boolean => (
  !!(sendEventsToAmount && window.amount_tag)
);

const utagEventEnabled = (eventDisabled?: IEventDisabled): boolean => (
  uTagEnabled() && eventDisabled?.tagDisabled !== TagsSupported.Utag
);

const amountEventEnabled = (eventDisabled?: IEventDisabled): boolean => (
  amountTagEnabled() && eventDisabled?.tagDisabled !== TagsSupported.AmountTag
);

class Tealium extends AbstractAnalyticsLogger {
  public constructor (applicationId?: string) {
    super(applicationId);

    // Removing the environment variable disables Tealium
    if (!TEALIUM_ENV) {
      return;
    }

    try {
      window.utag_data = window.utag_data || commonPageTags;
      if (!!siteType) {
        window.utag_data.site_type = siteType;
      }

      if (sendEventsToAmount) {
        window.amount_tag_data = window.amount_tag_data || window.utag_data;
        if (window.amount_tag_cfg_ovrd) {
          window.amount_tag_cfg_ovrd.noview = true;
        } else {
          window.amount_tag_cfg_ovrd = { noview: true };
        }
      }

      // Run utag.js
      /* eslint-disable */
      // @ts-ignore
      (function(a,b,c,d){a=`//tags.tiqcdn.com/utag/amount/${TENANT}/${TEALIUM_ENV}/utag.js`;b=document;c='script';d=b.createElement(c);d.src=a;d.type='text/java'+c;d.async=true;a=b.getElementsByTagName(c)[0];a.parentNode.insertBefore(d,a)})(); // prettier-ignore
    } catch (_e) {}
    /* eslint-enable */
  }

  public set applicationId (applicationId: string | undefined) {
    super.applicationId = applicationId;

    if (!TEALIUM_ENV) {
      return;
    }

    if (window.utag_data) {
      window.utag_data.application_id = applicationId;
    }

    if (sendEventsToAmount && window.amount_tag_data) {
      window.amount_tag_data.application_id = applicationId;
    }
  }

  public view (pageSpecificTags?: ITagInfo, eventDisabled?: IEventDisabled): void {
    if (!TEALIUM_ENV) {
      return;
    }

    const tags = this.mergeTags({ pageSpecificTags });

    if (utagEventEnabled(eventDisabled) && tenantUtagObjectName) {
      window[tenantUtagObjectName].view(tags);
    }

    if (amountEventEnabled(eventDisabled)) {
      window.amount_tag.view(tags);
    }
  }

  public sendEvent (
    eventTags?: ITagInfo, eventName?: string, eventDisabled?: IEventDisabled
  ): void {
    if (!TEALIUM_ENV) {
      return;
    }

    const tags = this.mergeTags({ eventTags });

    if (utagEventEnabled(eventDisabled) && tenantUtagObjectName) {
      window[tenantUtagObjectName].link(tags);
    }

    if (amountEventEnabled(eventDisabled)) {
      window.amount_tag.link(tags);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public sendClickEvent (): void {}

  private readonly mergeTags: (tags: ITealiumTags) => ITagInfo = ({
    pageSpecificTags = {},
    eventTags = {}
  }) => ({
    ...commonPageTags,
    ...pageSpecificTags,
    ...eventTags,
    internal_reference_id: super.applicationId,
    application_id: super.applicationId,
  })
}

export default Tealium;
